import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import './ParentWellnessNotifications.css';

function ParentWellnessNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [readNotifications, setReadNotifications] = useState(new Set());

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [filterType]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/v1/wellness/parent-notifications?type=${filterType}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setNotifications(res.data || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (notificationId) => {
    setReadNotifications(prev => new Set([...prev, notificationId]));
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/v1/wellness/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const unreadCount = notifications.filter(n => !readNotifications.has(n.id)).length;

  if (loading) {
    return <div className="notifications-loading">Loading wellness updates...</div>;
  }

  return (
    <div className="parent-notifications">
      <div className="notifications-header">
        <h2>🧘 Athlete Wellness Updates</h2>
        {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
      </div>

      {/* Filters */}
      <div className="notification-filters">
        <button
          className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
          onClick={() => setFilterType('all')}
        >
          All Updates
        </button>
        <button
          className={`filter-btn ${filterType === 'achievement' ? 'active' : ''}`}
          onClick={() => setFilterType('achievement')}
        >
          🏆 Achievements
        </button>
        <button
          className={`filter-btn ${filterType === 'concern' ? 'active' : ''}`}
          onClick={() => setFilterType('concern')}
        >
          ⚠️ Concerns
        </button>
        <button
          className={`filter-btn ${filterType === 'recovery' ? 'active' : ''}`}
          onClick={() => setFilterType('recovery')}
        >
          ✅ Recovery
        </button>
      </div>

      {/* Notifications List */}
      <div className="notifications-list">
        {notifications.length === 0 ? (
          <div className="empty-state">
            <p>📭 No wellness updates yet</p>
            <small>Check back soon for updates about your athlete's progress!</small>
          </div>
        ) : (
          notifications.map(notification => (
            <div
              key={notification.id}
              className={`notification-item ${notification.type} ${readNotifications.has(notification.id) ? 'read' : 'unread'}`}
              onMouseEnter={() => markAsRead(notification.id)}
            >
              <div className="notification-icon">{getNotificationIcon(notification.type)}</div>

              <div className="notification-content">
                <h3>{notification.title}</h3>
                <p>{notification.message}</p>

                {notification.data && (
                  <div className="notification-data">
                    {renderNotificationData(notification.type, notification.data)}
                  </div>
                )}

                <span className="notification-time">
                  {formatTime(notification.created_at)}
                </span>
              </div>

              <button
                className="btn-close-notification"
                onClick={() => deleteNotification(notification.id)}
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function getNotificationIcon(type) {
  switch (type) {
    case 'achievement':
      return '🏆';
    case 'concern':
      return '⚠️';
    case 'recovery':
      return '✅';
    case 'progress':
      return '📈';
    case 'milestone':
      return '🎯';
    default:
      return '📢';
  }
}

function renderNotificationData(type, data) {
  switch (type) {
    case 'achievement':
      return (
        <div className="data-grid">
          <div className="data-item">
            <span className="data-label">Skill</span>
            <span className="data-value">{data.skill_name}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Apparatus</span>
            <span className="data-value">{data.apparatus}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Difficulty</span>
            <span className="data-value">{data.difficulty}</span>
          </div>
        </div>
      );

    case 'concern':
      return (
        <div className="data-grid">
          <div className="data-item">
            <span className="data-label">Area</span>
            <span className="data-value">{data.concern_area}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Severity</span>
            <span className="data-value severity" style={{ color: getSeverityColor(data.severity) }}>
              {data.severity}/10
            </span>
          </div>
          {data.recommendation && (
            <div className="data-item full-width">
              <span className="data-label">Coach Recommendation</span>
              <span className="data-value">{data.recommendation}</span>
            </div>
          )}
        </div>
      );

    case 'recovery':
      return (
        <div className="data-grid">
          <div className="data-item">
            <span className="data-label">Recovery Status</span>
            <span className="data-value">{data.recovery_status}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Fatigue Level</span>
            <span className="data-value">{data.fatigue_level}/10</span>
          </div>
          <div className="data-item">
            <span className="data-label">Soreness</span>
            <span className="data-value">{data.soreness_level}/10</span>
          </div>
        </div>
      );

    case 'progress':
      return (
        <div className="data-grid">
          <div className="data-item">
            <span className="data-label">Metric</span>
            <span className="data-value">{data.metric_name}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Previous</span>
            <span className="data-value">{data.previous_value}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Current</span>
            <span className="data-value">{data.current_value}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Improvement</span>
            <span className="data-value improvement">+{data.improvement}%</span>
          </div>
        </div>
      );

    case 'milestone':
      return (
        <div className="milestone-container">
          <p className="milestone-text">{data.milestone_description}</p>
          <div className="milestone-badge">{data.milestone_name}</div>
        </div>
      );

    default:
      return null;
  }
}

function getSeverityColor(severity) {
  if (severity <= 3) return '#51cf66';
  if (severity <= 6) return '#ffd43b';
  return '#ff6b6b';
}

function formatTime(isoString) {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

export default ParentWellnessNotifications;
